<template>
  <nav v-if="count > limit && links.length && highestPage > 1" class="font-semibold leading-none">
    <ul class="flex flex-wrap items-center justify-center text-lg font-bold">
      <!-- Reduce by one page -->
      <li v-if="enablePrevious" class="c-pagination-list-item justify-start sm:justify-center" @click="onClick(currentPage - 1)" @click.prevent>
        <a :href="getHref(currentPage - 1)" class="c-pagination-list-item__content group cursor-pointer" :class="hoverClasses" :aria-label="t_('prev')">
          <AtomIconChevronLeft class="shrink-0 w-4" />
        </a>
      </li>
      <li v-for="(link, index) in links" :key="index" class="c-pagination-list-item justify-center" @click.capture="onClick(link.page)">
        <span v-if="link.type === 'dots'" class="pointer-events-none text-black">...</span>
        <a
          v-else
          :href="getHref(link.page)"
          class="c-pagination-list-item__content"
          :class="{
            'group cursor-pointer': link.type === 'link',
            [`${theme.background} ${theme.color} pointer-events-none`]: link.page === currentPage,
            [hoverClasses]: link.page !== currentPage,
          }"
          @click.prevent
        >
          <span class="c-pagination-page-number">{{ link.page }}</span>
        </a>
      </li>

      <!-- Increase by one page -->
      <li v-if="enableNext" class="c-pagination-list-item justify-end sm:justify-center" @click="onClick(currentPage + 1)">
        <a :href="getHref(currentPage + 1)" class="c-pagination-list-item__content group cursor-pointer" :class="hoverClasses" :aria-label="t_('next')" @click.prevent>
          <AtomIconChevronRight class="shrink-0 w-4" />
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { setupTranslationPrefixer } from '~/composables/util'

interface Props {
  count: number
  limit: number
  page: number
  showPrevious?: boolean
  showNext?: boolean
  theme?: {
    color: string
    colorHover?: string | null
    background: string
    backgroundHover?: string | null
  }
}

const emit = defineEmits(['click'])

const props = withDefaults(defineProps<Props>(), {
  showNext: true,
  showPrevious: true,
  theme: () => ({
    color: 'text-white',
    colorHover: null,
    background: 'bg-app-green',
  }),
})

const route = useRoute()
const highestPage = computed(() => Math.ceil(props.count / props.limit))
const currentPage = computed(() => Math.min(unref(highestPage), props.page))
const enablePrevious = computed(() => props.showPrevious && unref(currentPage) >= 2)
const enableNext = computed(() => props.showNext && unref(currentPage) < unref(highestPage))
const links = computed(() => {
  const currentPage_ = unref(currentPage)
  const highestPage_ = unref(highestPage)
  if (highestPage_ < 6) {
    return [...Array(highestPage_).keys()].map((offset) => ({ type: 'link', page: offset + 1 }))
  } else if (currentPage_ > 3 && currentPage_ < highestPage_ - 2) {
    return [{ type: 'dots' }, ...[-1, 0, 1].map((offset) => ({ type: 'link', page: currentPage_ + offset })), { type: 'dots' }]
  } else if (currentPage_ <= highestPage_ / 2) {
    return [...[1, 2, 3, 4].map((offset) => ({ type: 'link', page: offset })), { type: 'dots' }]
  } else {
    return [{ type: 'dots' }, ...[3, 2, 1, 0].map((offset) => ({ type: 'link', page: highestPage_ - offset }))]
  }
})

const hoverClasses = computed(() => {
  return [
    props.theme.colorHover ? props.theme.colorHover : props.theme.colorHover !== null ? props.theme.color : null,
    props.theme.backgroundHover ? props.theme.backgroundHover : props.theme.backgroundHover !== null ? props.theme.background : null,
  ]
    .filter((color) => color)
    .map((color) => `hover:${color}`)
    .join(' ')
})

// Emit a 'click' event to switch to a different page
const onClick = (targetPage: number) => {
  if (targetPage && targetPage !== unref(currentPage)) {
    emit('click', { page: Math.max(1, Math.min(unref(highestPage), unref(targetPage))) })
    window.scrollTo(0, 0)
  }
}

// Emit a 'click' event when one of the query parameters change
watch(
  () => route.query.page,
  (to, from) => {
    const normalizedToPage = Number.parseInt(Array.isArray(to) ? to[0] : to) || 1
    const normalizedFromPage = Number.parseInt(Array.isArray(from) ? from[0] : from) || 1
    if (normalizedToPage !== normalizedFromPage) {
      return
    }
    if (normalizedToPage === 1) {
      return
    }
    emit('click', { page: 1 })
    window.scrollTo(0, 0)
  },
)

/**
 * Returns a URL to a specific page, useful for crawlers
 * @param page Page number
 * @returns string
 */
const getHref = (page: number) => {
  const query = Object.entries({ ...route.query, page })
    .filter(([_key, value]) => !(value === undefined || value === ''))
    .filter(([key, value]) => !(key === 'page' && value === 1))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  return `${route.path}${query.length ? `?${query.join('&')}` : ''}`
}

const { t_ } = setupTranslationPrefixer('pagination')
</script>

<style lang="scss" scoped>
.c-pagination-list-item {
  @apply w-10 flex;

  @screen sm {
    @apply w-12;
  }
}

.c-pagination-list-item__content {
  @apply inline-flex justify-center items-center rounded-lg shrink-0 transform transition duration-200;
  width: 28px;
  height: 28px;
}

.c-pagination-page-number {
  margin-top: -2px;
}
</style>
